/**
 * Lector de exportaciones de APIZEAL (la plataforma de gestión de agencias que
 * usan muchos agentes hispanos en Florida).
 *
 * POR QUÉ NECESITA SU PROPIO CAMINO, como el de monday: **las cabeceras se
 * repiten**. "FIRST NAME" aparece seis veces en el mismo archivo, "IS APPLICANT"
 * otras seis. El importador normal mapea por NOMBRE de columna, así que ante
 * este archivo solo puede quedarse con una de las seis y descartar el resto —
 * no es que lo haga mal, es que la pregunta "¿qué columna es FIRST NAME?" no
 * tiene una sola respuesta aquí.
 *
 * LA FORMA DEL ARCHIVO (131 columnas, verificado contra el export real):
 *
 *   0 – 50    Cabecera fija: agente, TITULAR, direcciones y la póliza.
 *   51 – 130  Cinco bloques IDÉNTICOS de 16 columnas, uno por cada miembro
 *             adicional del hogar (cónyuge y dependientes, mezclados: quién es
 *             quién lo dice la columna RELATIONSHIP de su propio bloque).
 *
 * Por eso se lee por POSICIÓN y no por nombre. Las posiciones no se codifican a
 * mano: se localizan las repeticiones de "IS APPLICANT" y de ahí sale el tamaño
 * del bloque y cuántos hay, así que un export de Apizeal con seis miembros en
 * vez de cinco entra igual sin tocar nada.
 *
 * QUÉ DEVUELVE: filas con las MISMAS claves que ya consume el importador de
 * Cliente + Póliza (`firstName`, `spouseDob`, `dep2FullName`, `policyStatus`…).
 * Es un traductor, no una tubería nueva — así el archivo de Apizeal hereda gratis
 * todo lo que ya sabe hacer ese importador: detectar duplicados, separar nombres
 * con IA, los documentos migratorios, "¿fuma?", y crear la póliza.
 *
 * Y ADEMÁS, TODAS LAS COLUMNAS TAL CUAL (Lissette, 2026-09-03). Este lector
 * traduce las columnas que conoce; todo lo demás se lo tragaba en silencio. Si
 * Apizeal trae el estatus del cliente, el método de pago o quién lleva la
 * cuenta con un nombre de columna que aquí no está escrito, esa columna no
 * aparecía por ningún lado y no había forma de rescatarla ni a mano — que es
 * exactamente lo que pasó en la importación del 3 de septiembre.
 *
 * Ahora cada fila lleva TAMBIÉN sus columnas con el nombre que tienen en el
 * Excel: las de la cabecera fija con su nombre a secas, y las de cada miembro
 * del hogar como "M1 · DATE OF BIRTH", "M2 · SSN"… Así la pantalla de mapeo las
 * ofrece todas y no hay ni un dato del archivo que no se pueda colocar.
 *
 * Lo traducido NO cambia: sigue viniendo premapeado. Esto solo AÑADE lo que
 * antes se caía por el borde.
 */

// El parentesco lo traduce LA MISMA lista que usan los otros cuatro
// importadores. Aqui vivia una copia que no conocia la palabra "partner", y por
// eso una pareja terminaba en el bloque de dependientes (Lissette, 2026-09-15).
import { mapearParentesco } from './mapeosDeImportacion.js';

const norm = (v) => (v === null || v === undefined ? '' : String(v).trim());
const up = (v) => norm(v).toUpperCase();

/**
 * La cabecera SIN el prefijo de miembro que algunos exports le ponen delante.
 *
 * Lissette, 2026-10-16: *"cuando lo subimos no esta reconociendo las columnas
 * que dicen dependiente"*. Apizeal cambio la forma del bloque del hogar: antes
 * cada bloque repetia las mismas cabeceras ("FIRST NAME", "SSN"...), y ahora
 * cada una lleva el numero del miembro delante: "Dependent 1 FIRST NAME",
 * "Dependent 2 SSN". Como ya no hay dos cabeceras iguales, no habia repeticion
 * que medir: el libro ni se reconocia como de Apizeal y NO entraba ni un
 * conyuge ni un dependiente.
 *
 * Ademas el mismo export trae la palabra rota en una columna:
 * "MasculinoEMasculinoBER 1 GENDER" (era "MEMBER 1 GENDER" y a su "M" la
 * cambiaron por "Masculino"). Por eso el prefijo es CUALQUIER palabra seguida
 * del numero, y no una lista de palabras conocidas.
 *
 * Las cabeceras fijas que terminan en numero ("ADDRESS 1", "MAILING ADDRESS 2")
 * no se tocan: el prefijo exige texto DESPUES del numero.
 */
const baseName = (v) => up(v).replace(/^[A-Z][A-Z0-9-]*\s+\d{1,2}\s+(?=\S)/, '');

/** La cabecera del bloque que se repite por cada miembro del hogar. */
const MEMBER_ANCHOR = 'IS APPLICANT';

/**
 * ¿Es este libro un export de Apizeal?
 *
 * Firma: dos columnas que solo existen en Apizeal (son suyas, del flujo del
 * Marketplace) MÁS el ancla repetida. Se exigen las tres a la vez a propósito:
 * "IS APPLICANT" repetida por sí sola podría aparecer en cualquier hoja hecha a
 * mano, y quedarse con un archivo ajeno sería peor que no detectar el propio —
 * el usuario vería sus columnas leídas por posición y sin sentido.
 */
export function isApizealWorkbook(rows) {
  if (!Array.isArray(rows)) return false;
  for (let i = 0; i < Math.min(rows.length, 10); i++) {
    const h = (rows[i] || []).map(baseName);
    if (!h.length) continue;
    // La firma es: la columna del Marketplace MAS un bloque de hogar que se
    // REPITE. Las dos cosas juntas describen un export de ACA y no se dan por
    // casualidad en una hoja hecha a mano.
    //
    // Antes se exigia ademas "NPN USED IN MARKETPLACE" o "FFM USED IN
    // MARKETPLACE", y ESO es lo que dejaba fuera exports enteros de Apizeal:
    // el "Policies Full Export" no trae ninguna de las dos (trae `NPNinpolicy`
    // y `Agente en Poliza`). Comprobado con el archivo de Maria Linero,
    // 2026-09-03: 356 columnas, 393 filas, y la deteccion devolvia false.
    //
    // Tampoco se exige ya que el bloque se reconozca por "IS APPLICANT": hay
    // exports donde el bloque empieza en "RELATIONSHIP" y esa columna ni
    // siquiera existe. Vale cualquiera de las dos.
    const bloques = Math.max(
      h.filter((c) => c === MEMBER_ANCHOR).length,
      h.filter((c) => c === 'RELATIONSHIP').length,
    );
    if (bloques >= 2 && h.includes('MARKETPLACE ID')) return { headerRow: i };
  }
  return false;
}

/**
 * Localiza el bloque repetido. Devuelve dónde empieza el primer miembro, cuánto
 * mide un bloque y cuántos hay.
 *
 * La PRIMERA aparición de "IS APPLICANT" es la del titular, dentro de la
 * cabecera fija — no abre bloque. Los bloques empiezan en la segunda.
 */
function memberLayout(header) {
  // El bloque se deduce de la REPETICION, no de una columna concreta.
  //
  // Antes esto asumia dos cosas que solo valen para un export: que existe una
  // columna "IS APPLICANT", y que la PRIMERA es la del titular, o sea que los
  // bloques empiezan en la segunda. En el "Policies Full Export" las dos son
  // falsas: el titular no tiene esa columna, y el bloque empieza en
  // "RELATIONSHIP" con "IS APPLICANT" al FINAL. Resultado medido con el archivo
  // de Maria Linero (2026-09-03): los bloques se leian corridos 16 columnas y
  // salian 0 conyuges y 0 dependientes de 393 filas.
  //
  // Lo que SI es cierto en todos: a partir de un punto, un grupo de cabeceras
  // se repite cada N columnas. Eso se mide y ya.
  const nombres = header.map(baseName);

  // El tamano del bloque = la distancia que mas se repite entre dos apariciones
  // consecutivas de una misma cabecera.
  const donde = new Map();
  nombres.forEach((n, i) => { if (n) (donde.get(n) || donde.set(n, []).get(n)).push(i); });
  const votos = new Map();
  for (const idx of donde.values()) {
    for (let k = 1; k < idx.length; k++) {
      const d = idx[k] - idx[k - 1];
      if (d > 1) votos.set(d, (votos.get(d) || 0) + 1);
    }
  }
  let size = 0;
  let mejor = 0;
  for (const [d, v] of votos) if (v > mejor || (v === mejor && d < size)) { mejor = v; size = d; }
  if (!size) return null;

  // El bloque empieza en la PRIMERA columna que se repite a esa distancia.
  // Antes de ese punto esta la cabecera fija del titular.
  //
  // Se pide una TERCERA aparicion solo si la cabecera llega hasta ahi. Pedirla
  // siempre —como se hizo el 2026-09-03— deja fuera cualquier archivo con solo
  // DOS bloques (titular + un familiar, que es media cartera): no encontraba
  // donde empieza el bloque, devolvia null y el archivo entero salia con cero
  // filas. Con tres bloques o mas se sigue exigiendo la tercera, que es lo que
  // descarta que dos columnas de la cabecera fija coincidan por casualidad.
  let start = -1;
  for (let i = 0; i < header.length; i++) {
    const n = nombres[i];
    if (!n) continue;
    const hayTercero = i + 2 * size < nombres.length;
    if (nombres[i + size] === n && (!hayTercero || nombres[i + 2 * size] === n)) { start = i; break; }
  }
  if (start < 0) return null;

  // El ULTIMO bloque puede venir incompleto (al archivo de Maria le falta la
  // columna final del bloque 20). Se cuenta igual: perder cuatro personas por
  // una columna que falta seria peor que leer un bloque a medias, y las
  // columnas que no esten se leen vacias.
  const count = Math.ceil((header.length - start) / size);
  if (count < 1) return null;
  return { start, size, count };
}

/** Índice de cada cabecera DENTRO de la cabecera fija (la primera aparición). */
function fixedIndex(header, limit) {
  const idx = {};
  for (let i = 0; i < limit; i++) {
    const k = up(header[i]);
    if (k && !(k in idx)) idx[k] = i;
  }
  return idx;
}

/**
 * ¿El STATUS de Apizeal dice que la póliza está cancelada?
 *
 * Se queda en el mismo vocabulario que reconoce el importador aguas abajo
 * (`mapPolicyStatusIV` en ImportView, `importStatus` en el backend): si aquí se
 * diera por cancelada una póliza que allí acaba en otro estado, la ficha
 * quedaría con fecha de cancelación y estado activo, que es peor que no tenerla.
 */
function isCancelledStatus(v) {
  return /cancel|anulad|baja/i.test(norm(v));
}

/**
 * Nombre completo a partir de las cuatro columnas de Apizeal. Se junta en UNA
 * cadena porque el importador ya separa nombres con IA, y esa separación conoce
 * los apellidos compuestos hispanos mejor que un `join` a secas. Apizeal ya trae
 * los cuatro trozos separados, así que el orden va garantizado.
 */
function joinName(first, middle, last, second) {
  return [norm(first), norm(middle), norm(last), norm(second)].filter(Boolean).join(' ');
}

/**
 * Libro de Apizeal → filas con las claves del importador de Cliente + Póliza.
 *
 * @param {any[][]} rows Filas crudas de la hoja (cabecera incluida).
 * @param {number} headerRow Índice de la fila de cabecera (lo da isApizealWorkbook).
 * @param {number} maxDeps Cuántos dependientes ofrece el importador (MAX_IMPORT_DEPS).
 */
export function parseApizeal(rows, headerRow = 0, maxDeps = 10) {
  const header = rows[headerRow] || [];
  const layout = memberLayout(header);
  if (!layout) return { rows: [], members: 0, skippedMembers: 0, columnas: [] };

  const fx = fixedIndex(header, layout.start);
  const g = (row, key) => (fx[key] === undefined ? '' : norm(row[fx[key]]));

  // ── Nombre expuesto de CADA columna del archivo ────────────────────────────
  // La cabecera fija con su nombre tal cual; los bloques de miembro con
  // "M1 · ", "M2 · "… delante, porque ahi los nombres se repiten y sin el
  // prefijo serian seis columnas llamadas "SSN". Una cabecera repetida dentro
  // de la parte fija se numera ("STATUS (2)") en vez de perderse.
  const usados = new Set();
  const nombreDe = (bruto) => {
    let n = bruto;
    for (let k = 2; usados.has(n); k++) n = `${bruto} (${k})`;
    usados.add(n);
    return n;
  };
  const colFija = [];
  for (let i = 0; i < layout.start; i++) {
    const h = norm(header[i]);
    colFija.push(h ? nombreDe(h) : null);
  }
  const colMiembro = [];
  for (let b = 0; b < layout.count; b++) {
    // EL NOMBRE QUE TRAE EL EXCEL. Si el archivo ya numera el bloque
    // ("Dependent 1 FIRST NAME") se ensena asi, que es lo que la agente ve en
    // su Excel (Lissette, 2026-10-16). El prefijo se toma del que MAS se repite
    // en el bloque, no de cada columna, para que la columna rota de Apizeal
    // ("MasculinoEMasculinoBER 1 GENDER") salga como "Dependent 1 GENDER".
    // Los exports viejos no numeran nada: ahi se sigue poniendo "M1 · ".
    const votos = new Map();
    for (let k = 0; k < layout.size; k++) {
      const bruto = norm(header[layout.start + b * layout.size + k]);
      const base = baseName(bruto);
      const pre = bruto.slice(0, bruto.length - base.length).trim();
      if (pre) votos.set(pre, (votos.get(pre) || 0) + 1);
    }
    let prefijo = '';
    let mas = 0;
    for (const [pre, v] of votos) if (v > mas) { mas = v; prefijo = pre; }
    const fila = [];
    for (let k = 0; k < layout.size; k++) {
      const h = baseName(header[layout.start + b * layout.size + k]);
      fila.push(h ? nombreDe(prefijo ? `${prefijo} ${h}` : `M${b + 1} \u00b7 ${h}`) : null);
    }
    colMiembro.push(fila);
  }

  const out = [];
  const conDatos = new Set();
  let skippedMembers = 0;

  for (let r = headerRow + 1; r < rows.length; r++) {
    const row = rows[r] || [];
    if (!row.some((c) => norm(c))) continue; // fila vacía

    const o = {
      // ── Titular ──────────────────────────────────────────────────────────
      // FULL NAME cuando viene, porque la IA separa mejor un nombre hispano
      // completo que confiar en que las cuatro columnas estén bien repartidas.
      fullName: norm(g(row, 'FULL NAME')) ||
        joinName(g(row, 'FIRST NAME'), g(row, 'MIDDLE NAME'), g(row, 'LAST NAME'), g(row, 'SECOND LAST NAME')),
      phone: g(row, 'PHONE'),
      email: g(row, 'EMAIL'),
      dob: g(row, 'DATE OF BIRTH'),
      ssn: g(row, 'SSN'),
      gender: g(row, 'GENDER'),
      annualIncome: g(row, 'ANNUAL INCOME'),
      maritalStatus: g(row, 'MARITAL STATUS'),
      placeOfBirth: g(row, 'COUNTRY OF BIRTH'),
      immigrationStatus: g(row, 'IMMIGRATION STATUS'),
      // ADDRESS 2 (apto / suite) se pega a la calle: la ficha guarda una sola
      // línea de dirección y perderlo dejaría entregas a medias.
      addressStreet: [g(row, 'ADDRESS 1'), g(row, 'ADDRESS 2')].filter(Boolean).join(', '),
      addressCity: g(row, 'CITY'),
      addressState: g(row, 'STATE'),
      addressZip: g(row, 'ZIP'),
      county: g(row, 'COUNTY'),
      // Quien lleva la ficha. OJO: es el agente ASIGNADO al cliente, distinto
      // del agente EN PÓLIZA, que abajo sale de AGENT.
      assignedAgent: g(row, 'ASSIGNED TO'),
      // ATIENDE es la persona del equipo que lleva al cliente: va a "Atiende /
      // Handled by", no al agente asignado (Lissette, 2026-10-16). El
      // importador lo busca entre los asientos de la agencia por el nombre.
      attendedBy: g(row, 'ATIENDE'),

      // ── Póliza ───────────────────────────────────────────────────────────
      carrier: g(row, 'CARRIER/ISSUER'),
      plan: g(row, 'PLAN'),
      // El mismo cambio de Apizeal que renombro el bloque del hogar dejo esta
      // columna como "Dependent ID" (2026-10-16): es el Member ID de la poliza.
      memberId: g(row, 'MEMBER ID') || g(row, 'DEPENDENT ID'),
      monthlyPremium: g(row, 'MONTHLY PREMIUM'),
      subsidy: g(row, 'SUBSIDY'),
      effectiveDate: g(row, 'EFFECTIVE DATE'),
      // El "Health Policies" de 2026-10-16 no trae STATUS, solo POLICY STATUS
      // ("ACTIVO"): sin esto todas sus polizas entraban sin estado.
      policyStatus: g(row, 'STATUS') || g(row, 'POLICY STATUS'),
      agentLabel: g(row, 'AGENT'),
      producerCode: g(row, 'AGENT NPN'),
      planTotalCost: g(row, 'POLICY COST'),
      applicationNumber: g(row, 'MARKETPLACE ID'),
      saleDate: g(row, 'SUBMITTED'),
    };

    // Metodo de pago de la poliza ("Card", "Bank account", "Card, Bank
    // account"). "None" es la forma de Apizeal de decir que no hay: guardarlo
    // pintaria un metodo de pago llamado "None" en la tarjeta.
    const metodo = g(row, 'PAYMENT METHOD ON POLICY');
    if (metodo && !/^(none|n\/a|-)$/i.test(metodo)) o.paymentMethod = metodo;

    // CANCELLATION DATE en Apizeal es la fecha en que TERMINA la cobertura, no
    // "el cliente canceló". Las pólizas del Marketplace la traen puesta en el 31
    // de diciembre del año de plan mientras siguen perfectamente vigentes
    // (verificado con el export real de Clara Calle, 2026-08-24: STATUS "NEW" y
    // CANCELLATION DATE 2026-12-31 en la misma fila). Guardarla como fecha de
    // cancelación pintaría un 🛑 rojo sobre cada póliza activa de la cartera y
    // los reportes las contarían canceladas, así que va a RENOVACIÓN, que es lo
    // que de verdad significa (Lissette 2026-08-24).
    //
    // La excepción es la póliza que SÍ está cancelada: ahí esa misma fecha es la
    // cancelación de verdad, y además el backend la EXIGE cuando el estado es
    // cancelado — sin ella el importador degrada el estado a "pendiente" y se
    // pierde el dato. En ese caso se anota en las dos.
    const endDate = g(row, 'CANCELLATION DATE');
    if (endDate) {
      o.renewalDate = endDate;
      if (isCancelledStatus(o.policyStatus)) o.cancellationDate = endDate;
    }

    // Las direcciones postales solo se guardan si DIFIEREN de la de casa: si son
    // iguales, repetirlas en las notas es ruido en cada ficha.
    const mail = [g(row, 'MAILING ADDRESS 1'), g(row, 'MAILING ADDRESS 2'), g(row, 'MAILING CITY'),
      g(row, 'MAILING STATE'), g(row, 'MAILING ZIP')].filter(Boolean).join(', ');
    const home = [g(row, 'ADDRESS 1'), g(row, 'ADDRESS 2'), g(row, 'CITY'),
      g(row, 'STATE'), g(row, 'ZIP')].filter(Boolean).join(', ');
    const notes = [];
    if (mail && mail !== home) notes.push(`Dirección postal: ${mail}`);
    const internal = g(row, 'INTERNAL CODE');
    if (internal) notes.push(`Código interno Apizeal: ${internal}`);
    if (notes.length) o.notes = notes.join('\n');

    // ── Miembros del hogar ─────────────────────────────────────────────────
    // Se recorren los bloques por posición. El PRIMER "spouse" va a las columnas
    // de cónyuge de la ficha; el resto son dependientes numerados. Si vinieran
    // dos cónyuges (dato sucio), el segundo entra como dependiente en vez de
    // pisar al primero — perder una persona es peor que colocarla en otro sitio.
    //
    // Y "spouse" incluye A LA PAREJA. Este lector tenía su propia lista de
    // palabras y la palabra "partner" no estaba, así que un *Partner* se iba al
    // bloque de dependientes y el importador de después —que sí conoce
    // "pareja"— le ponía la etiqueta de cónyuge: una persona en la lista de
    // dependientes con el cartel de cónyuge, y las casillas del cónyuge vacías.
    // Es lo que se vio en la agencia de Denisse (Lissette, 2026-09-15).
    let depN = 0;
    let spouseTaken = false;
    for (let b = 0; b < layout.count; b++) {
      const base = layout.start + b * layout.size;
      const mg = (name) => {
        for (let k = 0; k < layout.size; k++) {
          if (baseName(header[base + k]) === name) return norm(row[base + k]);
        }
        return '';
      };
      // EL NOMBRE VA PARTIDO, PORQUE EL ARCHIVO YA LO TRAE PARTIDO.
      //
      // Lissette, 2026-09-04, con la cartera ya importada: *"no están ningún
      // dependiente datos ni cónyuge"*.
      //
      // Antes esto SOLO ponía el nombre entero (`spouseFullName`,
      // `depNFullName`), y el importador guarda al cónyuge y al dependiente
      // únicamente cuando tienen nombre o apellido POR SEPARADO:
      //
      //     if (sp.firstName || sp.lastName) { c.spouse = sp; ... }
      //
      // Entre medias hay un paso que parte el nombre entero con IA. Si ese paso
      // no corre —o parte mal—, el cónyuge y TODOS los dependientes se caen
      // enteros, con sus fechas, sus SSN y su parentesco. Sin un solo error: el
      // cliente entra, la familia no.
      //
      // Y lo absurdo es que no hacía falta partir nada: el export de Apizeal
      // trae FIRST NAME y LAST NAME en columnas distintas. Se juntaban aquí
      // para volver a separarlos después. Ahora van derechos, y el nombre
      // entero se sigue mandando por si alguien lo mapea a mano.
      const primero = mg('FIRST NAME');
      const medio = mg('MIDDLE NAME');
      const ultimo = joinName(mg('LAST NAME'), mg('SECOND LAST NAME'));
      const name = joinName(primero, medio, mg('LAST NAME'), mg('SECOND LAST NAME'));
      if (!name) continue; // bloque vacío: esta familia tiene menos miembros
      const palabraDeParentesco = mg('RELATIONSHIP');
      const rel = mapearParentesco(palabraDeParentesco) || 'dependent';

      if (rel === 'spouse' && !spouseTaken) {
        spouseTaken = true;
        // LA PALABRA CON LA QUE EL ARCHIVO NOMBRÓ A ESTA PERSONA VIAJA CON ELLA.
        //
        // De ella depende el estado civil: "unión libre" si decía *pareja*,
        // "casado/a" si decía *cónyuge*. La decisión NO se toma aquí porque el
        // estado civil que trae la hoja está en el idioma de la hoja ("Married",
        // "Soltero") y hasta el importador no se traduce al valor del CRM —
        // decidir con el texto crudo compararía "Married" con "married" y daría
        // el resultado contrario.
        o.spouseRelation = palabraDeParentesco;
        o.spouseFullName = name;
        o.spouseFirstName = primero;
        if (medio) o.spouseMiddleName = medio;
        o.spouseLastName = ultimo;
        o.spouseDob = mg('DATE OF BIRTH');
        o.spouseSsn = mg('SSN');
        o.spousePhone = mg('PHONE');
        o.spouseEmail = mg('EMAIL');
        o.spouseGender = mg('GENDER');
        o.spouseIncome = mg('ANNUAL INCOME');
        o.spouseImmigrationStatus = mg('IMMIGRATION STATUS');
        continue;
      }

      depN += 1;
      if (depN > maxDeps) { skippedMembers += 1; continue; }
      o[`dep${depN}FullName`] = name;
      o[`dep${depN}FirstName`] = primero;
      if (medio) o[`dep${depN}MiddleName`] = medio;
      o[`dep${depN}LastName`] = ultimo;
      o[`dep${depN}Dob`] = mg('DATE OF BIRTH');
      o[`dep${depN}Ssn`] = mg('SSN');
      o[`dep${depN}Gender`] = mg('GENDER');
      o[`dep${depN}Income`] = mg('ANNUAL INCOME');
      o[`dep${depN}Immigration`] = mg('IMMIGRATION STATUS');
      o[`dep${depN}Relation`] = mg('RELATIONSHIP');
    }

    // Y las columnas crudas, al lado de lo ya traducido. Nunca pisan una clave
    // nuestra: si un nombre de columna coincidiera con una (no puede pasar hoy
    // —las nuestras son camelCase y las suyas MAYUSCULAS— pero no se deja al
    // azar), se guarda aparte en vez de sobrescribirla, que es la unica forma
    // de que no se pierda ninguna de las dos.
    const crudas = [];
    for (let i = 0; i < colFija.length; i++) crudas.push([colFija[i], norm(row[i])]);
    for (let b = 0; b < colMiembro.length; b++) {
      const base = layout.start + b * layout.size;
      for (let k = 0; k < colMiembro[b].length; k++) crudas.push([colMiembro[b][k], norm(row[base + k])]);
    }
    for (const [nombre, valor] of crudas) {
      if (!nombre || !valor) continue;
      const destino = nombre in o ? `${nombre} (columna)` : nombre;
      o[destino] = valor;
      conDatos.add(destino);
    }

    out.push(o);
  }

  // Solo se ofrecen las columnas que traen algo en alguna fila: una lista de
  // 131 huecos vacios no ayuda a nadie a encontrar la que busca.
  const columnas = [];
  for (const n of colFija) if (n && conDatos.has(n)) columnas.push(n);
  for (const fila of colMiembro) for (const n of fila) if (n && conDatos.has(n)) columnas.push(n);
  for (const n of conDatos) if (!columnas.includes(n)) columnas.push(n);

  return { rows: out, members: layout.count, skippedMembers, columnas };
}

// ── Lo que se ensena en la pantalla de mapeo ──────────────────────────────────
//
// Las claves que este lector YA coloco (`spouseFirstName`, `dep2Dob`…) salian
// en la pantalla de mapeo con ese nombre de programador, y parecian columnas
// del Excel que no existen. Lissette, 2026-10-16: *"esta mapeando spouse con
// columna que no existe"*. Ahora se dice en espanol que es y de donde sale.
// Solo cambia el TEXTO: el valor que se mapea sigue siendo la clave.

const PARTE = {
  FullName: ['nombre completo', 'full name'],
  FirstName: ['nombre', 'first name'],
  MiddleName: ['segundo nombre', 'middle name'],
  LastName: ['apellidos', 'last name'],
  Dob: ['fecha de nacimiento', 'date of birth'],
  Ssn: ['SSN', 'SSN'],
  Phone: ['teléfono', 'phone'],
  Email: ['correo', 'email'],
  Gender: ['género', 'gender'],
  Income: ['ingreso', 'income'],
  ImmigrationStatus: ['estatus migratorio', 'immigration status'],
  Immigration: ['estatus migratorio', 'immigration status'],
  Relation: ['parentesco', 'relationship'],
};

const TITULAR_Y_POLIZA = {
  fullName: ['👤 Titular — nombre completo', '👤 Primary — full name'],
  phone: ['👤 Titular — teléfono', '👤 Primary — phone'],
  email: ['👤 Titular — correo', '👤 Primary — email'],
  dob: ['👤 Titular — fecha de nacimiento', '👤 Primary — date of birth'],
  ssn: ['👤 Titular — SSN', '👤 Primary — SSN'],
  gender: ['👤 Titular — género', '👤 Primary — gender'],
  annualIncome: ['👤 Titular — ingreso anual', '👤 Primary — annual income'],
  maritalStatus: ['👤 Titular — estado civil', '👤 Primary — marital status'],
  placeOfBirth: ['👤 Titular — país de nacimiento', '👤 Primary — country of birth'],
  immigrationStatus: ['👤 Titular — estatus migratorio', '👤 Primary — immigration status'],
  addressStreet: ['👤 Titular — dirección', '👤 Primary — street'],
  addressCity: ['👤 Titular — ciudad', '👤 Primary — city'],
  addressState: ['👤 Titular — estado', '👤 Primary — state'],
  addressZip: ['👤 Titular — ZIP', '👤 Primary — ZIP'],
  county: ['👤 Titular — condado', '👤 Primary — county'],
  assignedAgent: ['👤 Titular — agente asignado', '👤 Primary — assigned agent'],
  attendedBy: ['👤 Titular — atiende / handled by', '👤 Primary — handled by'],
  notes: ['👤 Titular — notas', '👤 Primary — notes'],
  carrier: ['📄 Póliza — aseguradora', '📄 Policy — carrier'],
  plan: ['📄 Póliza — plan', '📄 Policy — plan'],
  memberId: ['📄 Póliza — Member ID', '📄 Policy — member ID'],
  monthlyPremium: ['📄 Póliza — prima mensual', '📄 Policy — monthly premium'],
  subsidy: ['📄 Póliza — subsidio', '📄 Policy — subsidy'],
  effectiveDate: ['📄 Póliza — fecha de efectividad', '📄 Policy — effective date'],
  policyStatus: ['📄 Póliza — estado', '📄 Policy — status'],
  agentLabel: ['📄 Póliza — agente en póliza', '📄 Policy — agent on policy'],
  producerCode: ['📄 Póliza — NPN del agente', '📄 Policy — agent NPN'],
  planTotalCost: ['📄 Póliza — costo total', '📄 Policy — total cost'],
  applicationNumber: ['📄 Póliza — Marketplace ID', '📄 Policy — marketplace ID'],
  saleDate: ['📄 Póliza — fecha de venta', '📄 Policy — sale date'],
  renewalDate: ['📄 Póliza — renovación', '📄 Policy — renewal'],
  cancellationDate: ['📄 Póliza — fecha de cancelación', '📄 Policy — cancellation date'],
  paymentMethod: ['📄 Póliza — método de pago', '📄 Policy — payment method'],
};

/**
 * Texto para la pantalla de mapeo de una clave que este lector ya coloco.
 * Devuelve la clave tal cual si no es una de las suyas (una columna del Excel
 * se ensena con su propio nombre).
 */
export function etiquetaDeClaveApizeal(clave, lang = 'es') {
  const i = lang === 'es' ? 0 : 1;
  const k = String(clave || '');
  if (TITULAR_Y_POLIZA[k]) return `⚡ ${TITULAR_Y_POLIZA[k][i]}`;
  let m = k.match(/^spouse([A-Z]\w*)$/);
  if (m && PARTE[m[1]]) {
    return i === 0
      ? `⚡ 💍 Cónyuge — ${PARTE[m[1]][0]} (del Dependent que dice Spouse)`
      : `⚡ 💍 Spouse — ${PARTE[m[1]][1]} (from the Dependent marked Spouse)`;
  }
  m = k.match(/^dep(\d+)([A-Z]\w*)$/);
  if (m && PARTE[m[2]]) {
    return i === 0
      ? `⚡ 👶 Dependiente ${m[1]} — ${PARTE[m[2]][0]} (de los Dependent que no son Spouse)`
      : `⚡ 👶 Dependent ${m[1]} — ${PARTE[m[2]][1]} (from the Dependents not marked Spouse)`;
  }
  return k;
}
